import { useEffect, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, ImageSquare, Spinner } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}

const EMPTY = {
  title: "", category: "A Memory / Story", memory: "", institution: "", campus: "",
  country: "", state: "", city: "", class_batch: "", graduation_year: "", period: "", year: "", people_shown: "",
};

export default function MemoryContributeDialog({ open, onOpenChange, onCreated, defaultCategory, defaultInstitution }) {
  const [f, setF] = useState(EMPTY);
  const [photoIds, setPhotoIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => { api.get("/public/memories/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (open && defaultCategory) setF((s) => ({ ...s, category: defaultCategory })); }, [open, defaultCategory]);
  useEffect(() => { if (open && defaultInstitution) setF((s) => ({ ...s, institution: defaultInstitution })); }, [open, defaultInstitution]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isAward = f.category === "Trophies & Awards";

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const file of files) ids.push(await uploadPhoto(file));
      setPhotoIds((p) => [...p, ...ids]);
      toast.success(`${ids.length} photo${ids.length > 1 ? "s" : ""} attached`);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Give your memory a title");
    if (!f.memory.trim() && photoIds.length === 0) return toast.error("Add a story or at least one photo");
    setBusy(true);
    try {
      const tags = [f.category, f.country, f.city, f.institution, f.period].filter(Boolean);
      const { data } = await api.post("/memories", { ...f, photo_file_ids: photoIds, tags });
      toast.success("Thank you — your memory is now part of Memory Lane");
      setF(EMPTY); setPhotoIds([]);
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="memory-contribute-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">Share a school or college memory</DialogTitle>
          <DialogDescription>Preserve the little things — a uniform, a classroom, an old bag, a teacher, a story. Add a photo and tell us about it.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Title *</Label>
              <Input value={f.title} onChange={set("title")} placeholder="Our blue school bags from the 1980s" data-testid="memory-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="memory-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Institution</Label>
              <Input value={f.institution} onChange={set("institution")} placeholder="Delhi Public School" data-testid="memory-institution" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{isAward ? "Notes" : "Your memory"}</Label>
            <Textarea rows={4} value={f.memory} onChange={set("memory")} placeholder={isAward ? "What was the award for? The competition, the achievement, the story behind it…" : "Tell the story behind it — what it was, who was there, what you remember…"} data-testid="memory-story" />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Country</Label><Input value={f.country} onChange={set("country")} placeholder="India" data-testid="memory-country" /></div>
            <div className="space-y-1.5"><Label>City</Label><Input value={f.city} onChange={set("city")} placeholder="Kochi" /></div>
            <div className="space-y-1.5"><Label>Year{isAward ? " won" : " / Decade"}</Label><Input value={f.period} onChange={set("period")} placeholder={isAward ? "1992" : "1985 or 1980s"} data-testid="memory-period" /></div>
            <div className="space-y-1.5"><Label>Class / Batch</Label><Input value={f.class_batch} onChange={set("class_batch")} placeholder="Class of 1988" /></div>
            <div className="space-y-1.5"><Label>Campus</Label><Input value={f.campus} onChange={set("campus")} placeholder="Main campus" /></div>
            <div className="space-y-1.5"><Label>{isAward ? "Student / team names" : "People in the photo"}</Label><Input value={f.people_shown} onChange={set("people_shown")} placeholder={isAward ? "The winning team / student(s)" : "Optional"} data-testid="memory-people" /></div>
          </div>

          {/* Photos */}
          <div className="space-y-2">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-3">
              {photoIds.map((id) => (
                <div key={id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border/60">
                  <img src={fileUrl(id)} alt="memory" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setPhotoIds((p) => p.filter((x) => x !== id))}
                    className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white"><X size={12} /></button>
                </div>
              ))}
              <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-lg border border-dashed border-border/70 text-muted-foreground hover:border-secondary/50 hover:text-secondary" data-testid="memory-photo-input">
                {uploading ? <Spinner size={20} className="animate-spin" /> : <ImageSquare size={22} />}
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} disabled={uploading} />
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Only upload photos you have the right to share. Historical class photos, uniforms, buildings and school supplies are welcome.</p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={busy || uploading} className="rounded-full gap-1.5" data-testid="memory-submit">
            <Plus size={16} /> {busy ? "Sharing…" : "Share memory"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
